import { useCompanySettings } from "hooks/useCompanySettings";
import { t } from "i18next";
import { useMemo } from "react";
import type { CustomFieldResource } from "store/customFieldsStore";
import { useAppSelector } from "store/store";
import type { TPortalType } from "store/types";
import { EPortalType } from "store/types";

import { channelVariableLabel, channelVariablePlaceholder } from "./channelVariable";

// Placeholders that open a secondary picker instead of being inserted directly.
export const VARIABLES_WITH_SECONDARY_CHOICE: ReadonlySet<string> = new Set(["{{channel:XX}}"]);

export const agreementVariables = (
    isPreferredIdentifierConcat = true,
): { [key: string]: { [key: string]: string } } => {
    return {
        [EPortalType.BOOK]: {
            "{{channel:XX}}": t("ContractSettings.Book.VariableChannel"),
            "{{company}}": t("ContractSettings.Book.VariableCompanyName"),
            "{{companyHQ}}": t("ContractSettings.Book.VariableCompanyAddress"),
            "{{name}}": t("ContractSettings.VariableBeneficiaryFullName"),
            "{{firstName}}": t("ContractSettings.VariableBeneficiaryFirstName"),
            "{{lastName}}": t("ContractSettings.VariableBeneficiaryLastName"),
            "{{firstNameLegalEntity}}": t("ContractSettings.VariableLegalEntityFirstName"),
            "{{lastNameLegalEntity}}": t("ContractSettings.VariableLegalEntityLastName"),
            "{{birthDate}}": t("ContractSettings.VariableBirthDate"),
            "{{address}}": t("ContractSettings.Book.VariableAddress"),
            "{{title}}": t("ContractSettings.Book.VariableTitle"),
            "{{workCode}}": t("ContractSettings.Book.VariableWorkCode"),
            "{{associatedBrand}}": t("ContractSettings.Book.VariableAssociatedBrand"),
            "{{ean13}}": t("ContractSettings.VariableEan13"),
            "{{articleTitle}}": t("ContractSettings.Book.VariableArticleTitle"),
            "{{articleReleaseDate}}": t("ContractSettings.Book.VariableArticleReleaseDate"),
            "{{articleFormat}}": t("ContractSettings.Book.VariableArticleFormat"),
            "{{periodicite}}": t("ContractSettings.Book.VariablePeriodicite"),
            "{{advance}}": t("ContractSettings.Book.VariableAdvance"),
            "{{initialAdvance}}": t("ContractSettings.Book.VariableInitialAdvance"),
            "{{duration}}": t("ContractSettings.Book.VariableDuration"),
            "{{endDate}}": t("ContractSettings.VariableEndDate"),
            "{{initialDraw}}": t("ContractSettings.Book.VariableInitialDraw"),
            "{{beneficiarySiret}}": t("ContractSettings.VariableBeneficiarySiret"),
            "{{beneficiaryNIR}}": t("ContractSettings.VariableBeneficiaryNIR"),
            "{{beneficiaryEmail}}": t("ContractSettings.VariableBeneficiaryEmail"),
            "{{beneficiaryPhone}}": t("ContractSettings.VariableBeneficiaryPhone"),
            "{{beneficiaryIBAN}}": t("ContractSettings.VariableBeneficiaryIBAN"),
            "{{beneficiarySWIFT}}": t("ContractSettings.VariableBeneficiarySWIFT"),
            // "{{manuscriptDeliveryDate}}": t("ContractSettings.Book.VariableManuscriptDeliveryDate"),
            // "{{renewDuration}}": t("ContractSettings.Book.VariableRenewDuration"),
            // "{{freeUnits}}": t("ContractSettings.Book.VariableFreeUnits"),
            // "{{plannedReleaseDate}}": t("ContractSettings.Book.VariablePlannedReleaseDate"),
            // "{{authorOrderPublicPricePercent}}": t("ContractSettings.Book.VariableAuthorOrderPublicPricePercent"),
        },
        [EPortalType.PRESS]: {
            "{{name}}": t("Accounting.VariableName"),
            "{{firstName}}": t("ContractSettings.VariableBeneficiaryFirstName"),
            "{{lastName}}": t("ContractSettings.VariableBeneficiaryLastName"),
            "{{birthDate}}": t("ContractSettings.VariableBirthDate"),
            "{{netToPay}}": t("ContractSettings.VariableNetToPay"),
            "{{grossDuty}}": t("ContractSettings.VariableGrossDuty"),
            "{{siret}}": t("ContractSettings.VariableSiret"),
            "{{reference}}": t("ContractSettings.VariableReference"),
            "{{address}}": t("ContractSettings.VariableAddress"),
            "{{article}}": t("ContractSettings.VariableArticle"),
            "{{publication}}": t("ContractSettings.VariableIssue"),
            "{{company}}": t("ContractSettings.VariableCompany"),
            "{{issn}}": isPreferredIdentifierConcat
                ? t("ContractSettings.VariableISSN")
                : t("ContractSettings.VariableUniqueCode"),
            "{{beneficiarySiret}}": t("ContractSettings.VariableBeneficiarySiret"),
            "{{beneficiaryNIR}}": t("ContractSettings.VariableBeneficiaryNIR"),
            "{{beneficiaryEmail}}": t("ContractSettings.VariableBeneficiaryEmail"),
            "{{beneficiaryPhone}}": t("ContractSettings.VariableBeneficiaryPhone"),
            "{{beneficiaryIBAN}}": t("ContractSettings.VariableBeneficiaryIBAN"),
            "{{beneficiarySWIFT}}": t("ContractSettings.VariableBeneficiarySWIFT"),
        },
        [EPortalType.AUDIOVISUAL]: {
            "{{name}}": t("Accounting.VariableName"),
            "{{firstName}}": t("ContractSettings.VariableBeneficiaryFirstName"),
            "{{lastName}}": t("ContractSettings.VariableBeneficiaryLastName"),
            "{{birthDate}}": t("ContractSettings.VariableBirthDate"),
            "{{netToPay}}": t("ContractSettings.VariableNetToPay"),
            "{{grossDuty}}": t("ContractSettings.VariableGrossDuty"),
            "{{siret}}": t("ContractSettings.VariableSiret"),
            "{{reference}}": t("ContractSettings.VariableReference"),
            "{{address}}": t("ContractSettings.VariableAddress"),
            "{{article}}": t("ContractSettings.VariableService"),
            "{{publication}}": t("ContractSettings.VariableProduction"),
            "{{company}}": t("ContractSettings.VariableCompany"),
            "{{issn}}": t("ContractSettings.VariableUniqueCode"),
            "{{beneficiarySiret}}": t("ContractSettings.VariableBeneficiarySiret"),
            "{{beneficiaryNIR}}": t("ContractSettings.VariableBeneficiaryNIR"),
            "{{beneficiaryEmail}}": t("ContractSettings.VariableBeneficiaryEmail"),
            "{{beneficiaryPhone}}": t("ContractSettings.VariableBeneficiaryPhone"),
            "{{beneficiaryIBAN}}": t("ContractSettings.VariableBeneficiaryIBAN"),
            "{{beneficiarySWIFT}}": t("ContractSettings.VariableBeneficiarySWIFT"),
        },
    };
};

export type AgreementVariables = Record<string, string>;

// Variables the framework-contract gather can't populate (no title/term backing).
const FRAMEWORK_CONTRACT_HIDDEN_VARIABLES: ReadonlySet<string> = new Set([
    "{{ean13}}",
    "{{periodicite}}",
    "{{duration}}",
    "{{initialDraw}}",
]);

// Press/audiovisual framework-contract: also hide article-scoped variables the gather can't populate.
const FRAMEWORK_CONTRACT_HIDDEN_PRESS_VARIABLES: ReadonlySet<string> = new Set([
    "{{article}}",
    "{{publication}}",
    "{{issn}}",
    "{{netToPay}}",
    "{{grossDuty}}",
    "{{siret}}",
    "{{reference}}",
]);

// Press framework-contract: extra variables the gather populates from configuration.
const frameworkContractPressExtraVariables = (): AgreementVariables => ({
    "{{folioPrice}}": t("TermTemplates.PressFolioPrice"),
    "{{workNature}}": t("TermTemplates.PressWorkNature"),
});

// Framework-contract (both book and press): validity period, populated from
// configuration.startDate / configuration.endDate by the backend gather.
const frameworkContractPeriodExtras = (): AgreementVariables => ({
    "{{startDate}}": t("ContractSettings.VariableStartDate"),
    "{{endDate}}": t("ContractSettings.VariableEndDate"),
});

const metadataResourceLabel = (relatedResource: CustomFieldResource, portalType: TPortalType) => {
    if (relatedResource === "beneficiary") return t("CustomFields.BeneficiaryPage");
    if (relatedResource === "term") return t("CustomFields.TermPage");
    return t(`CustomFields.TitlePage_${portalType}`);
};

/**
 * Get all variables with translations including meta
 */
export const useAgreementVariables = (
    portalType: TPortalType,
    { isFrameworkContract = false }: { isFrameworkContract?: boolean } = {},
) => {
    const { customFields } = useAppSelector((state) => state.customFields);
    const { channels, channelCompensationGroups } = useAppSelector((state) => state.channels);
    const formatTypes = useAppSelector((state) => state.titleFormatTypes.items);
    const { getCompanySettings } = useCompanySettings();
    // Preferred identifier - ISSN or internal code:
    const preferredIdentifier = getCompanySettings("preferred_title_identifier");
    const isPreferredIdentifierConcat = preferredIdentifier === "concat";

    const isPressPortal = portalType === EPortalType.PRESS || portalType === EPortalType.AUDIOVISUAL;

    return useMemo(() => {
        const baseVariables: AgreementVariables = agreementVariables(isPreferredIdentifierConcat)[portalType];
        const variables: AgreementVariables = isFrameworkContract
            ? {
                  ...Object.fromEntries(
                      Object.entries(baseVariables).filter(
                          ([key]) =>
                              !FRAMEWORK_CONTRACT_HIDDEN_VARIABLES.has(key) &&
                              !(isPressPortal && FRAMEWORK_CONTRACT_HIDDEN_PRESS_VARIABLES.has(key)),
                      ),
                  ),
                  ...(isPressPortal ? frameworkContractPressExtraVariables() : {}),
                  ...frameworkContractPeriodExtras(),
              }
            : { ...baseVariables };
        const channelsVariables: AgreementVariables = {};

        // Add meta data variables translation to agreement variables list.
        // Framework contracts have no title, so hide title-scoped custom fields.
        customFields.forEach((field) => {
            if (isFrameworkContract && field.relatedResource === "title") return;
            const resource = metadataResourceLabel(field.relatedResource, portalType);
            const metaVariableLabel = `${t("ContractSettings.MetadataResource", { resource })} ${field.name}`;
            variables[`{{meta:${field.uuid}}}`] = metaVariableLabel;
        });

        channels.forEach((channel) => {
            channelsVariables[channelVariablePlaceholder("channel", channel.id, null)] = channel.name;
            formatTypes.forEach((formatType) => {
                channelsVariables[channelVariablePlaceholder("channel", channel.id, formatType.id)] =
                    channelVariableLabel(channel.name, formatType.name);
            });
        });
        // Framework contract: no channelGroup hydration in the PDF gather.
        if (!isFrameworkContract) {
            channelCompensationGroups.forEach((group) => {
                channelsVariables[channelVariablePlaceholder("channelGroup", group.id, null)] = group.name;
                formatTypes.forEach((formatType) => {
                    channelsVariables[channelVariablePlaceholder("channelGroup", group.id, formatType.id)] =
                        channelVariableLabel(group.name, formatType.name);
                });
            });
        }

        // Populate variables for LexicalVariableNode.ts
        agreementVariablesList.variablesLabels = { ...variables, ...channelsVariables };

        return {
            // All variables list for agreements including meta variables
            variables,
            // All channels variables list
            channelsVariables,
        };
    }, [
        channelCompensationGroups,
        channels,
        customFields,
        formatTypes,
        isFrameworkContract,
        isPreferredIdentifierConcat,
        isPressPortal,
        portalType,
    ]);
};

/**
 * Because we can't call React hooks inside class obj components -->
 * Prepare variables to be used in LexicalVariableNode.ts to fix old meta/channels variables translation
 * It's getting populated from the useAgreementVariables() upside
 */
export const agreementVariablesList = { variablesLabels: {} as AgreementVariables };
